import { SphereGeometry } from 'shared/geometries/SphereGeometry';
import { isPlanogramVersionCluster, isPlanogramVersionCurve, isPlanogramVersionImage, isPlanogramVersionProduct, isPlanogramVersionShape, isPlanogramVersionTextArea, isPlanogramVersionVideo, isProductOverlayAction } from 'shared/interfaces/planogram';
import { Color, Vector2 } from 'three';
import ImageObject3D from 'shared/object3d/ImageObject3D';
import ShapeObject3D from 'shared/object3d/ShapeObject3D';
import TextAreaObject3D from 'shared/object3d/TextAreaObject3D';
import VideoObject3D from 'shared/object3d/VideoObject3D';
import CurveObject3D from 'shared/object3d/CurveObject3D';
import PlanogramPoint from 'shared/utils/PlanogramPoint';
import { sphereEventHandler } from './custom_event_utils';
import { SPHERE_EVENT_NAMES } from './event-names';
import { Metrics } from './metrics';
import { AppState } from './shared/app.state';
import { MATOMO_EVENT_NAMES } from './metric-events';
import { PLANOGRAM_CONFIG } from 'shared/config/PlanogramConfig';
export const MAX_ITEM_RENDER_ORDER = 999999;
// TODO: reuse with editor/src/utils/rendering-engine.utils.ts
function subVirtualSphereY(y) {
    return y - PLANOGRAM_CONFIG.height / 2 - PLANOGRAM_CONFIG.virtualSphereY;
}
export class SphereItem {
    overrideSize(width, height) {
        this.width = width;
        this.height = height;
        this.updateAzimuthStartRadians();
    }
    updateAzimuthStartRadians() {
        this.azimuthStartRadians = SphereGeometry.calcAzimuthStartRadians(this.x, Math.max(0, this.width), this.planogram.width);
    }
    constructor(params, planogram, lodProvider) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        this.planogram = planogram;
        this.itemData = params;
        this.data = params.data;
        this.id = params.id;
        this.x = params.x;
        this.y = subVirtualSphereY(params.y);
        this.width = params.width;
        this.height = params.height;
        this.visible = params.visible;
        this.updateAzimuthStartRadians();
        this.layer = params.layer;
        if (params.action !== undefined && isProductOverlayAction(params.action)) {
            this.data.product = {
                id: ((_a = params.action.data) === null || _a === void 0 ? void 0 : _a.productId) || ((_b = this.data.product) === null || _b === void 0 ? void 0 : _b.id),
                identifier: ((_c = params.action.data) === null || _c === void 0 ? void 0 : _c.productIdentifier) || ((_d = this.data.product) === null || _d === void 0 ? void 0 : _d.identifier),
                name: ((_e = params.action.data) === null || _e === void 0 ? void 0 : _e.productName) || ((_f = this.data.product) === null || _f === void 0 ? void 0 : _f.name)
            };
        }
        if ((_h = (_g = params.data) === null || _g === void 0 ? void 0 : _g.product) === null || _h === void 0 ? void 0 : _h.name) {
            this.name = params.data.product.name;
        }
        if ((_k = (_j = params.data) === null || _j === void 0 ? void 0 : _j.product) === null || _k === void 0 ? void 0 : _k.identifier) {
            this.identifier = params.data.product.identifier;
        }
        else if ((_m = (_l = params.data) === null || _l === void 0 ? void 0 : _l.product) === null || _m === void 0 ? void 0 : _m.id) {
            this.identifier = params.data.product.id.toString();
        }
        if (params.action) {
            this.action = params.action;
        }
        this.type = params.type;
        this.createObject3D(lodProvider);
    }
    createObject3D(lodProvider) {
        var _a, _b;
        const palette = this.planogram.palette;
        const itemData = this.itemData;
        const position = this.makePosition();
        const scale = [itemData.width, itemData.height, 1];
        if (isPlanogramVersionImage(itemData) || isPlanogramVersionProduct(itemData)) {
            const object3D = new ImageObject3D();
            const config = Object.assign(Object.assign({}, itemData.data), { builderData: {}, position,
                scale });
            object3D.configure(config, lodProvider.getMaterial(this.itemData.id));
            this.object3D = object3D;
        }
        else if (isPlanogramVersionShape(itemData) || isPlanogramVersionCluster(itemData)) {
            const object3D = new ShapeObject3D();
            const config = Object.assign(Object.assign({}, itemData.data), { builderData: {}, position,
                scale, cornerRadius: itemData.data.individualCornerRadius });
            object3D.configure(config);
            object3D.rebuild(config, palette);
            this.object3D = object3D;
        }
        else if (isPlanogramVersionTextArea(itemData)) {
            const object3D = new TextAreaObject3D();
            const config = Object.assign(Object.assign({}, itemData.data), { builderData: {}, position,
                scale, nameManuallyDefined: false });
            object3D.configure(config, palette);
            this.object3D = object3D;
        }
        else if (isPlanogramVersionCurve(itemData)) {
            const object3D = new CurveObject3D();
            const config = Object.assign(Object.assign({}, itemData.data), { builderData: {}, position });
            // curve scale is not the same as object size
            scale.splice(0, undefined, ...itemData.data.scale);
            object3D.configure(config);
            object3D.rebuild(config, palette);
            this.object3D = object3D;
        }
        else if (isPlanogramVersionVideo(itemData)) {
            const object3D = new VideoObject3D();
            object3D.setPlaceholderColor(new Color('#ffffff'), 0);
            const config = Object.assign(Object.assign({}, itemData.data), { builderData: {}, position,
                scale, fullSize: (_a = itemData.data.fullSize) !== null && _a !== void 0 ? _a : false, naturalResolution: (_b = itemData.data.naturalResolution) !== null && _b !== void 0 ? _b : [scale[0], scale[1]] });
            object3D.configure(config);
            object3D.hideControls(config.hideControls);
            object3D.addEventListener('VideoShare', () => {
                sphereEventHandler.emit(SPHERE_EVENT_NAMES.VIDEO.SHARE, this);
            });
            object3D.userData.pointerdown = () => {
                Metrics.storeTheEvent(AppState.planogramName, 'click', MATOMO_EVENT_NAMES.WEBGL_CLICK_VIDEO(itemData.name, itemData.data.videoUrl, Math.round(object3D.playTime)));
            };
            this.object3D = object3D;
        }
        // TODO: add Group and SocialFeed?
        else {
            throw new Error(`Unsupported item ${itemData.id} of type ${itemData.type}`);
        }
        this.object3D.position.set(position.x, position.y, 1);
        this.object3D.scale.set(...scale);
        this.object3D.renderOrder = MAX_ITEM_RENDER_ORDER - itemData.layer;
        this.object3D.visible = itemData.visible;
        this.object3D.userData.component = this;
    }
    hasInput() {
        return this.action !== undefined;
    }
    getPosition() {
        return new Vector2(this.x, this.y);
    }
    getSize() {
        return new Vector2(Math.abs(this.width), Math.abs(this.height));
    }
    getViewportCenter() {
        // TODO: why is the offset still necessary?
        return new Vector2(this.x + 0.5 * Math.abs(this.width), this.y + 0.5 * Math.abs(this.height) + this.planogram.height * 0.25);
    }
    makePosition() {
        return new PlanogramPoint({
            x: this.x + 0.5 * Math.abs(this.width),
            y: this.y + 0.5 * Math.abs(this.height)
        });
    }
    getCenter(sphereShape) {
        const planogramCenter = this.getViewportCenter();
        const sphereCenter = sphereShape.fromPlanogramCoordinateViewer(planogramCenter, this.planogram.size());
        return [sphereShape.sample(sphereCenter), sphereShape.normalAt(sphereCenter).multiplyScalar(-1)];
    }
    dispose() {
        var _a, _b;
        // @ts-ignore
        (_b = (_a = this.object3D) === null || _a === void 0 ? void 0 : _a.dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
}
